import { useEffect } from 'react'
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { trackPageView } from './analytics'
import { AuthProvider } from './context/AuthContext'
import { LeagueProvider } from './context/LeagueContext'
import ProtectedRoute from './components/ProtectedRoute'
import ErrorBoundary from './components/ErrorBoundary'
import HiddenRoute from './components/HiddenRoute'
import CookieConsent from './components/CookieConsent'
import { JAI_COLORS } from './components/JaiBrand'
import { useRememberAppPath } from './utils/appReturn'
import { IS_NATIVE_APP } from './services/runtime'
import Landing from './pages/Landing'
import NativeWelcome from './pages/NativeWelcome'
import Waitlist from './pages/Waitlist'
import Pricing from './pages/Pricing'
import Support from './pages/Support'
import SupportThanks from './pages/SupportThanks'
import Login from './pages/Login'
import Signup from './pages/Signup'
import VerifyEmail from './pages/VerifyEmail'
import ForgotPassword from './pages/ForgotPassword'
import ResetPassword from './pages/ResetPassword'
import BillingSuccess from './pages/BillingSuccess'
import BillingTopupSuccess from './pages/BillingTopupSuccess'
import BillingCancel from './pages/BillingCancel'
import Terms from './pages/Terms'
import Privacy from './pages/Privacy'
import ChatPage from './pages/ChatPage'
import SelectLeagues from './pages/SelectLeagues'
import LeagueHome from './pages/LeagueHome'
import LeaguePage from './pages/LeaguePage'
import TeamNewsPage from './pages/TeamNewsPage'
import LeagueNewsPage from './pages/LeagueNewsPage'
import DraftBoard from './pages/DraftBoard'
import StrategyEstablishment from './pages/draft/StrategyEstablishment'
import LiveDraft from './pages/draft/LiveDraft'
import DraftReview from './pages/draft/DraftReview'
import TradeAnalyser from './pages/TradeAnalyser'
import AnalyseTrade from './pages/trade/AnalyseTrade'
import TradeRadar from './pages/trade/TradeRadar'
import WaiverWire from './pages/WaiverWire'
import PlayerAnalyser from './pages/PlayerAnalyser'
import TeamAnalyser from './pages/TeamAnalyser'
import NotesPage from './pages/NotesPage'
import NoteDetail from './pages/NoteDetail'
import GameNightPage from './pages/GameNightPage'
import LeagueNewspaper from './pages/LeagueNewspaper'
import ChampionshipOdds from './pages/ChampionshipOdds'
import OpponentScout from './pages/OpponentScout'
import BenchRegret from './pages/BenchRegret'
import FantasyDnaPage from './pages/FantasyDnaPage'
import ScheduleSurgeon from './pages/ScheduleSurgeon'
import GrudgeMatch from './pages/GrudgeMatch'
import PowerRankings from './pages/PowerRankings'
import TrashTalk from './pages/TrashTalk'
import InjuryInsurance from './pages/InjuryInsurance'
import CrystalBall from './pages/CrystalBall'

// Renders nothing — it just notes which app screen the user is on, so the
// public pages (Pricing, Terms, Privacy) can send them back to it instead of
// to the marketing landing page. See utils/appReturn.js.
function RememberAppPath() {
  useRememberAppPath()
  return null
}

// Also renders nothing. React Router changes the URL without reloading the
// page, so GA would otherwise only ever see the very first screen of a visit.
// This reports each one as the user moves between them. See src/analytics.js.
function TrackPageViews() {
  const location = useLocation()
  useEffect(() => {
    trackPageView(location.pathname + location.search)
  }, [location.pathname, location.search])
  return null
}

export default function App() {
  return (
    <div style={{ background: JAI_COLORS.paper, minHeight: 'calc(100vh / var(--jai-zoom))' }}>
      <AuthProvider>
        <LeagueProvider>
          <BrowserRouter>
          <ErrorBoundary>
          <RememberAppPath />
          <TrackPageViews />
          <Routes>
            {/* The front door is the APP, not a marketing page. A visitor with
                no session gets a guest account automatically (see
                ProtectedRoute) and lands in Ask JAI, so the first thing anyone
                sees is the product rather than a pitch and a sign-up form. */}
            <Route path="/"                element={<Navigate to="/chat" replace />} />

            {/* Public — marketing landing. Still here, just no longer the front
                door: campaign links, ads and search results can point at
                /welcome when a pitch page is what's wanted. */}
            {/* Full marketing page on web; a compact welcome screen on
                native — see NativeWelcome.jsx for why (Guideline 4.2). */}
            <Route path="/welcome"         element={IS_NATIVE_APP ? <NativeWelcome /> : <Landing />} />
            {/* Guideline 3.1.1: the App Store build sells nothing, so the
                Pricing route (and its Stripe billing return URLs below)
                simply doesn't exist on native — there is nothing to route
                to, dead or otherwise. Every link that used to point here is
                gated at its own call site (grep 'pricing' across
                frontend/src to audit). Native and web share this file, so
                the gate has to be a runtime check, not two build targets. */}
            {!IS_NATIVE_APP && <Route path="/pricing" element={<Pricing />} />}
            {/* Support JAI: optional one-off payment. Web only, like /pricing
                (Guideline 3.1.1) — unregistered on native, so the catch-all
                below sends it to /chat. Public: works signed out. */}
            {!IS_NATIVE_APP && <Route path="/support" element={<Support />} />}
            {!IS_NATIVE_APP && <Route path="/support/thanks" element={<SupportThanks />} />}

            {/* Public — July marketing campaign links here, not "/" */}
            <Route path="/waitlist"        element={<Waitlist />} />

            {/* Public */}
            <Route path="/login"           element={<Login />} />
            <Route path="/signup"          element={<Signup />} />
            <Route path="/verify"          element={<VerifyEmail />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password"  element={<ResetPassword />} />

            {/* Public — legal */}
            <Route path="/terms"           element={<Terms />} />
            <Route path="/privacy"         element={<Privacy />} />

            {/* Billing — return URLs from Stripe Checkout. Not registered on
                native — see the /pricing note above. */}
            {!IS_NATIVE_APP && (
              <Route path="/billing/success" element={
                <ProtectedRoute allowWhileSelectingLeagues><BillingSuccess /></ProtectedRoute>
              } />
            )}
            {!IS_NATIVE_APP && (
              <Route path="/billing/topup/success" element={
                <ProtectedRoute allowWhileSelectingLeagues><BillingTopupSuccess /></ProtectedRoute>
              } />
            )}
            {!IS_NATIVE_APP && <Route path="/billing/cancel" element={<BillingCancel />} />}

            {/* Protected — league downgrade/selection screen (see AuthContext
                needsLeagueSelection). Must stay reachable even while blocked —
                it IS the page that clears the block. */}
            <Route path="/select-leagues" element={
              <ProtectedRoute allowWhileSelectingLeagues><SelectLeagues /></ProtectedRoute>
            } />

            {/* Protected — main chat */}
            <Route path="/chat" element={
              <ProtectedRoute><ChatPage /></ProtectedRoute>
            } />

            {/* Protected — league home screen */}
            <Route path="/league/:id" element={
              <ProtectedRoute><LeagueHome /></ProtectedRoute>
            } />

            {/* Protected — league news pages */}
            <Route path="/league/:id/team-news" element={
              <ProtectedRoute><HiddenRoute path="team-news"><TeamNewsPage /></HiddenRoute></ProtectedRoute>
            } />
            <Route path="/league/:id/league-news" element={
              <ProtectedRoute><HiddenRoute path="league-news"><LeagueNewsPage /></HiddenRoute></ProtectedRoute>
            } />

            {/* Protected — league sub-pages */}
            <Route path="/league/:id/roster" element={
              <ProtectedRoute><LeaguePage tab="roster" /></ProtectedRoute>
            } />
            <Route path="/league/:id/players" element={
              <ProtectedRoute><LeaguePage tab="players" /></ProtectedRoute>
            } />
            <Route path="/league/:id/draft" element={
              <ProtectedRoute><DraftBoard /></ProtectedRoute>
            } />
            <Route path="/league/:id/draft/strategy" element={
              <ProtectedRoute><StrategyEstablishment /></ProtectedRoute>
            } />
            <Route path="/league/:id/draft/live" element={
              <ProtectedRoute><LiveDraft /></ProtectedRoute>
            } />
            <Route path="/league/:id/draft/review" element={
              <ProtectedRoute><DraftReview /></ProtectedRoute>
            } />
            <Route path="/league/:id/trade" element={
              <ProtectedRoute><TradeAnalyser /></ProtectedRoute>
            } />
            <Route path="/league/:id/trade/analyse" element={
              <ProtectedRoute><AnalyseTrade /></ProtectedRoute>
            } />
            <Route path="/league/:id/trade/radar" element={
              <ProtectedRoute><TradeRadar /></ProtectedRoute>
            } />
            <Route path="/league/:id/waiver" element={
              <ProtectedRoute><WaiverWire /></ProtectedRoute>
            } />
            <Route path="/league/:id/player-analyser" element={
              <ProtectedRoute><PlayerAnalyser /></ProtectedRoute>
            } />
            <Route path="/league/:id/team-analyser" element={
              <ProtectedRoute><TeamAnalyser /></ProtectedRoute>
            } />
            <Route path="/league/:id/notes" element={
              <ProtectedRoute><NotesPage /></ProtectedRoute>
            } />
            <Route path="/league/:id/notes/:noteId" element={
              <ProtectedRoute><NoteDetail /></ProtectedRoute>
            } />
            <Route path="/league/:id/game-night" element={
              <ProtectedRoute><HiddenRoute path="game-night"><GameNightPage /></HiddenRoute></ProtectedRoute>
            } />
            <Route path="/league/:id/newspaper" element={
              <ProtectedRoute><HiddenRoute path="newspaper"><LeagueNewspaper /></HiddenRoute></ProtectedRoute>
            } />
            <Route path="/league/:id/championship-odds" element={
              <ProtectedRoute><HiddenRoute path="championship-odds"><ChampionshipOdds /></HiddenRoute></ProtectedRoute>
            } />
            <Route path="/league/:id/opponent-scout" element={
              <ProtectedRoute><OpponentScout /></ProtectedRoute>
            } />
            <Route path="/league/:id/bench-regret" element={
              <ProtectedRoute><HiddenRoute path="bench-regret"><BenchRegret /></HiddenRoute></ProtectedRoute>
            } />
            <Route path="/league/:id/fantasy-dna" element={
              <ProtectedRoute><HiddenRoute path="fantasy-dna"><FantasyDnaPage /></HiddenRoute></ProtectedRoute>
            } />
            <Route path="/league/:id/schedule-surgeon" element={
              <ProtectedRoute><ScheduleSurgeon /></ProtectedRoute>
            } />
            <Route path="/league/:id/grudge-match" element={
              <ProtectedRoute><HiddenRoute path="grudge-match"><GrudgeMatch /></HiddenRoute></ProtectedRoute>
            } />
            <Route path="/league/:id/power-rankings" element={
              <ProtectedRoute><HiddenRoute path="power-rankings"><PowerRankings /></HiddenRoute></ProtectedRoute>
            } />
            <Route path="/league/:id/trash-talk" element={
              <ProtectedRoute><HiddenRoute path="trash-talk"><TrashTalk /></HiddenRoute></ProtectedRoute>
            } />
            <Route path="/league/:id/injury-insurance" element={
              <ProtectedRoute><InjuryInsurance /></ProtectedRoute>
            } />
            <Route path="/league/:id/crystal-ball" element={
              <ProtectedRoute><HiddenRoute path="crystal-ball"><CrystalBall /></HiddenRoute></ProtectedRoute>
            } />

            {/* Default */}
            <Route path="*" element={<Navigate to="/chat" replace />} />
          </Routes>
          {/* A cookie banner inside a native app reads as "this is a website
              in a box" — the loudest possible 4.2 signal. PostHog still runs
              on native without it; that's disclosed in the App Store privacy
              nutrition label instead (see Part 7 of the App Store plan). */}
          {!IS_NATIVE_APP && <CookieConsent />}
          </ErrorBoundary>
          </BrowserRouter>
        </LeagueProvider>
      </AuthProvider>
    </div>
  )
}
